import React from 'react';
import logo from '../../../assets/images/carepay_logo_white.png';

const formatDoSharedTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return ` | ${day}-${month}-${year} | ${time}`;
};

const Row = ({ label, value, bold = false }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.3rem 0',
    }}
  >
    <div
      style={{
        fontSize: '1.05rem',
        color: '#2c2c2c',
        fontWeight: bold ? 600 : 300,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: '1.05rem',
        color: '#2c2c2c',
        fontWeight: bold ? 600 : 300,
        textAlign: 'right',
      }}
    >
      {value}
    </div>
  </div>
);

const generateDisbursalOrderReceipt = (data) => {
  const transactionRows = [
    ['Transaction amount', data.transactionAmount, true],
    ['Payment plan', data.paymentPlan, false],
    ['Advance amount to be collected', data.advanceAmount, true],
    ['Platform charges to merchant', data.platformCharges, false],
    ['GST on platform charges', data.gst, false],
    ...(Number(data.tcsAmountRaw) > 0
      ? [['Tax Collected at Source (1%)', data.tcsAmount, false]]
      : []),
    ['Payment to merchant', data.paymentToMerchant, false],
    ['PF amount', data.pfAmount, false],
    ['Financier name', data.financierName, false],
  ];

  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
        fontFamily:
          "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        backgroundColor: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '3px',
        textAlign: 'left',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '600px',
          backgroundColor: '#fffbf9',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            backgroundColor: '#514c9f',
            color: 'white',
            padding: '1rem 2rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'end',
              marginBottom: '0.7rem',
            }}
          >
            <img src={logo} alt="CarePay" style={{ width: '100px', height: 'auto' }} />
            <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>support@carepay.money</div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'end',
            }}
          >
            <div
              style={{
                fontSize: '1.9rem',
                fontWeight: 600,
                letterSpacing: '0.03em',
              }}
            >
              Disbursal Order
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 500 }}>ID: {data.orderId}</div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#ffb47d',
            padding: '0.6rem 2rem',
            fontSize: '0.95rem',
            fontWeight: 600,
            color: '#2c2c2c',
            wordBreak: 'break-word',
          }}
        >
          To - {data.merchantName}
          {formatDoSharedTimestamp(data.doSharedTimeStamp)}
        </div>

        <div style={{ padding: '1.5rem 2rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '1.5rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.3rem',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  fontSize: '0.9rem',
                  color: '#2c2c2c',
                  fontWeight: 600,
                  marginBottom: '0.1rem',
                }}
              >
                Patient name
              </div>
              <div
                style={{
                  fontSize: '1.3rem',
                  color: '#2c2c2c',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  letterSpacing: '0.05rem',
                  wordBreak: 'break-word',
                }}
              >
                {data.patientName || '—'}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.3rem',
              marginBottom: '1.5rem',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                fontSize: '0.9rem',
                color: '#2c2c2c',
                fontWeight: 600,
                marginBottom: '0.1rem',
              }}
            >
              Treatment name
            </div>
            <div
              style={{
                fontSize: '1.3rem',
                color: '#2c2c2c',
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: '0.05rem',
                wordBreak: 'break-word',
              }}
            >
              {data.treatmentName || '—'}
            </div>
          </div>

          <div
            style={{
              fontSize: '1.15rem',
              fontWeight: 600,
              color: '#2c2c2c',
              textAlign: 'center',
              margin: '1.5rem -2rem 1rem -2rem',
              padding: '0.6rem 2rem',
              background: 'linear-gradient(90deg, #fffbf9 0%, #ecebff 50%, #fffbf9 100%)',
            }}
          >
            Customer details
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.2rem',
              marginBottom: '1.5rem',
            }}
          >
            <Row label="Customer name" value={data.customerName || '—'} bold />
            <Row label="Contact number" value={data.contactNumber || '—'} bold />
          </div>

          <div
            style={{
              fontSize: '1.15rem',
              fontWeight: 600,
              color: '#2c2c2c',
              textAlign: 'center',
              margin: '1.5rem -2rem 1rem -2rem',
              padding: '0.6rem 2rem',
              background: 'linear-gradient(90deg, #fffbf9 0%, #ecebff 50%, #fffbf9 100%)',
            }}
          >
            Transaction details
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.15rem',
              marginBottom: '1rem',
            }}
          >
            {transactionRows.map(([label, value, bold]) => (
              <Row key={label} label={label} value={value || '—'} bold={bold} />
            ))}
          </div>

          <div
            style={{
              backgroundColor: '#ffe9db',
              padding: '1.2rem 2rem',
              margin: '1.5rem -2rem 0',
              textAlign: 'center',
              borderBottom: 'solid 0.9rem #ffb47d',
            }}
          >
            <div
              style={{
                fontSize: '1rem',
                color: '#2c2c2c',
                fontWeight: 600,
                marginBottom: '0.6rem',
              }}
            >
              Payment to the merchant after platform charges settlement
            </div>
            <div
              style={{
                fontSize: '1.6rem',
                color: '#2c2c2c',
                fontWeight: 600,
              }}
            >
              {data.paymentToMerchant}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default generateDisbursalOrderReceipt;
